import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUIState } from '../contexts/UIStateContext';
import Sidebar from '../components/Sidebar';
import AdminSidebar from '../components/AdminSidebar';
import UserDetailView from '../components/admin/UserDetailView';
import InvitationDetailView from '../components/admin/InvitationDetailView';
import ProductDetailView from '../components/admin/ProductDetailView';
import CreateProductModal from '../components/admin/CreateProductModal';
import RoleDetailView from '../components/admin/RoleDetailView';
import PermissionDetailView from '../components/admin/PermissionDetailView';
import ModelDetailView from '../components/admin/ModelDetailView';
import AIParametersModal from '../components/admin/AIParametersModal';
import AIConnectionsModal from '../components/admin/AIConnectionsModal';
import AIModelsModal from '../components/admin/AIModelsModal';
import DeleteConfirmationModal from '../components/shared/DeleteConfirmationModal';
import FloatingActionMenu from '../components/shared/FloatingActionMenu';
import { adminApi } from '../lib/admin-api';
import useAdminSection from '../hooks/useAdminSection';
import { useAdminDelete } from '../hooks/useAdminDelete';
import { useKnowledgeData } from '../hooks/useKnowledgeData';
import customToast from '../utils/toast';
import { getNextTheme } from '../constants/subThemes';
import { IconButton } from '../components/shared/StandardButtons';

export default function Admin() {
  const navigate = useNavigate();
  const {
    getAdminActiveTab,
    setAdminActiveTab,
    getAdminSelectedConfig,
    setAdminSelectedConfig,
    getAdminSelectedUser,
    setAdminSelectedUser,
    getAdminSelectedInvitation,
    setAdminSelectedInvitation,
    getAdminSelectedProduct,
    setAdminSelectedProduct,
    getAdminSelectedRole,
    setAdminSelectedRole,
    getAdminSelectedPermission,
    setAdminSelectedPermission,
  } = useUIState();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => window.innerWidth <= 980);
  const [activeTab, setActiveTab] = useState(() => getAdminActiveTab());
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [actionButtonTrigger, setActionButtonTrigger] = useState({ buttonId: null, timestamp: 0 });

  // Knowledge data for FloatingActionMenu
  const { collections, products: knowledgeProducts, fetchCollections, fetchProducts } = useKnowledgeData({
    fetchOnMount: false
  });

  // AI Configuration section using hook
  const {
    items: modelConfigs,
    selectedId: selectedConfigId,
    selectedItem: selectedConfiguration,
    isLoadingList: isLoadingOverview,
    isLoadingDetail: isLoadingConfig,
    overviewData: aiOverview,
    setSelectedId: setSelectedConfigId,
    refreshOverview: refreshAiOverview,
    refreshDetail: refreshConfiguration,
    refreshBoth: refreshConfigurationAndOverview
  } = useAdminSection({
    tabName: 'ai',
    apiOverview: adminApi.modelConfigurations.getOverview,
    apiDetail: adminApi.modelConfigurations.getDetail,
    getInitialSelectedId: getAdminSelectedConfig,
    setPersistedSelectedId: setAdminSelectedConfig,
    extractList: (data) => data?.model_configurations,
    activeTab
  });

  // Parameters modal state
  const [showParametersModal, setShowParametersModal] = useState(false);

  // Connections modal state
  const [showConnectionsModal, setShowConnectionsModal] = useState(false);

  // Models modal state
  const [showModelsModal, setShowModelsModal] = useState(false);

  // User Management state
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(() => getAdminSelectedUser());
  const [selectedUser, setSelectedUser] = useState(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [selectedInvitationId, setSelectedInvitationId] = useState(() => getAdminSelectedInvitation());
  const [selectedInvitation, setSelectedInvitation] = useState(null);

  // Product Management section using hook
  const {
    items: products,
    selectedId: selectedProductId,
    selectedItem: selectedProduct,
    isLoadingList: isLoadingProducts,
    isLoadingDetail: isLoadingProduct,
    setSelectedId: setSelectedProductId,
    refreshOverview: refreshProductsOverview,
    refreshDetail: refreshProductDetail
  } = useAdminSection({
    tabName: 'products',
    apiOverview: adminApi.products.getOverview,
    apiDetail: adminApi.products.getDetail,
    getInitialSelectedId: getAdminSelectedProduct,
    setPersistedSelectedId: setAdminSelectedProduct,
    extractList: (data) => data?.products,
    activeTab
  });

  const [showCreateProductModal, setShowCreateProductModal] = useState(false);

  // Roles Management section using hook
  const {
    items: roles,
    selectedId: selectedRoleId,
    selectedItem: selectedRole,
    isLoadingList: isLoadingRoles,
    isLoadingDetail: isLoadingRole,
    setSelectedId: setSelectedRoleId,
    refreshOverview: refreshRolesOverview,
    refreshDetail: refreshRoleDetail
  } = useAdminSection({
    tabName: 'roles',
    apiOverview: adminApi.roles.getOverview,
    apiDetail: adminApi.roles.getDetail,
    getInitialSelectedId: getAdminSelectedRole,
    setPersistedSelectedId: setAdminSelectedRole,
    extractList: (data) => data?.roles,
    activeTab
  });

  // Permissions Management section using hook
  const {
    items: permissions,
    selectedId: selectedPermissionId,
    selectedItem: selectedPermission,
    isLoadingList: isLoadingPermissions,
    isLoadingDetail: isLoadingPermission,
    setSelectedId: setSelectedPermissionId,
    refreshOverview: refreshPermissionsOverview,
    refreshDetail: refreshPermissionDetail
  } = useAdminSection({
    tabName: 'permissions',
    apiOverview: adminApi.permissions.getOverview,
    apiDetail: adminApi.permissions.getDetail,
    getInitialSelectedId: getAdminSelectedPermission,
    setPersistedSelectedId: setAdminSelectedPermission,
    extractList: (data) => data?.permissions,
    listKey: 'id', // permissions use 'id' field as the identifier
    activeTab
  });

  // Unified delete hooks for each resource type
  const { handleDelete: deleteUser, deleteModal: userDeleteModal } = useAdminDelete({
    resourceType: 'user',
    getItemName: (user) => user?.fullName || user?.username || 'User',
    onSuccess: async () => {
      setSelectedUserId(null);
      setSelectedUser(null);
      await refreshUsersOverview();
    }
  });

  const { handleDelete: deleteRole, deleteModal: roleDeleteModal } = useAdminDelete({
    resourceType: 'role',
    getItemName: (role) => role?.name || 'Role',
    onSuccess: async () => {
      setSelectedRoleId(null);
      setSelectedRole(null);
      await refreshRolesOverview();
    }
  });

  const { handleDelete: deleteInvitation, deleteModal: invitationDeleteModal } = useAdminDelete({
    resourceType: 'invitation',
    getItemName: () => 'Invitation',
    onSuccess: async () => {
      setSelectedInvitationId(null);
      setSelectedInvitation(null);
      await refreshInvitations();
    }
  });

  const { handleDelete: deleteProduct, deleteModal: productDeleteModal } = useAdminDelete({
    resourceType: 'product',
    getItemName: (product) => product?.name || 'Product',
    onSuccess: async () => {
      setSelectedProductId(null);
      await refreshProductsOverview();
    }
  });

  const { handleDelete: deleteConfiguration, deleteModal: configDeleteModal } = useAdminDelete({
    resourceType: 'configuration',
    getItemName: (config) => config?.name || 'Configuration',
    onSuccess: async () => {
      setSelectedConfigId(null);
      await refreshAIOverview();
    }
  });

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reusable function to refresh users overview
  const refreshUsersOverview = async () => {
    try {
      setIsLoadingUsers(true);
      const overview = await adminApi.users.getOverview();

      // Attach theme to each user based on their position
      // Split into active/inactive to match sidebar display order
      const rawUsers = overview.users || [];
      const activeUsers = rawUsers.filter(u => u.is_active);
      const inactiveUsers = rawUsers.filter(u => !u.is_active);

      // Assign themes based on display order
      const usersWithTheme = [
        ...activeUsers.map((user, idx) => ({ ...user, theme: getNextTheme(idx) })),
        ...inactiveUsers.map((user, idx) => ({ ...user, theme: getNextTheme(activeUsers.length + idx) }))
      ];

      setUsers(usersWithTheme);

      // Validate selected user against available users
      if (selectedUserId && usersWithTheme.length > 0) {
        const userExists = usersWithTheme.some(user => user.id === selectedUserId);
        if (!userExists) {
          handleSetSelectedUser(null);
        }
      }
    } catch (error) {
      console.error('Failed to fetch users overview:', error);
      setUsers([]);
    } finally {
      setIsLoadingUsers(false);
    }
  };

  // Reusable function to refresh invitations
  const refreshInvitations = async () => {
    try {
      const invitationsData = await adminApi.invitations.getAll(false, false); // Not used, not expired
      // Attach theme to each invitation
      const invitationsWithTheme = (invitationsData || []).map((inv, idx) => ({
        ...inv,
        theme: getNextTheme(idx)
      }));
      setInvitations(invitationsWithTheme);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
      setInvitations([]);
    }
  };

  // Fetch users overview and invitations when switching to users tab
  useEffect(() => {
    if (activeTab === 'users') {
      refreshUsersOverview();
      refreshInvitations();
    }
  }, [activeTab]);

  // Reusable function to refresh current user detail
  const refreshUserDetail = async () => {
    if (!selectedUserId) {
      setSelectedUser(null);
      return;
    }

    try {
      setIsLoadingUser(true);
      const user = await adminApi.users.getDetail(selectedUserId);

      // Find theme from the list item and attach it to detail
      const listUser = users.find(u => u.id === selectedUserId);
      if (listUser?.theme) {
        user.theme = listUser.theme;
      }

      setSelectedUser(user);
    } catch (error) {
      console.error('Failed to fetch user detail:', error);
      setSelectedUser(null);
    } finally {
      setIsLoadingUser(false);
    }
  };

  // Fetch user detail when selectedUserId changes (only if on users tab)
  // BUT only if no invitation is selected
  useEffect(() => {
    if (activeTab === 'users' && !selectedInvitationId) {
      refreshUserDetail();
    }
  }, [selectedUserId, activeTab, selectedInvitationId, users]);

  // Update selected invitation when selectedInvitationId changes
  useEffect(() => {
    if (selectedInvitationId && activeTab === 'users') {
      const invitation = invitations.find(inv => inv.id === selectedInvitationId);
      // Theme is already attached to invitation from the list, no need to copy
      setSelectedInvitation(invitation || null);
    } else {
      setSelectedInvitation(null);
    }
  }, [selectedInvitationId, invitations, activeTab]);

  // Wrapper to persist active tab to UIState
  const handleSetActiveTab = (tab) => {
    setActiveTab(tab);
    setAdminActiveTab(tab);
  };

  // Wrapper to persist selected user to UIState
  const handleSetSelectedUser = (userId) => {
    setSelectedUserId(userId);
    setAdminSelectedUser(userId);
    // Clear invitation selection when selecting a user
    setSelectedInvitationId(null);
    setSelectedInvitation(null);
    setAdminSelectedInvitation(null); // Also clear from UIState
  };

  // Handler for selecting an invitation
  const handleSetSelectedInvitation = (invitationId) => {
    setSelectedInvitationId(invitationId);
    setAdminSelectedInvitation(invitationId);
    // Clear user selection when selecting an invitation
    setSelectedUserId(null);
    setSelectedUser(null);
    setAdminSelectedUser(null);
  };

  // Handle user updates
  const handleUserUpdate = async (updatedUser) => {
    try {
      await adminApi.users.update(updatedUser.id, updatedUser);
      // Refresh user detail and overview to sync
      await refreshUserDetail();
      await refreshUsersOverview();
    } catch (error) {
      console.error('Failed to update user:', error);
      throw error; // Let the component handle the error
    }
  };

  // Delete handlers - now using unified delete hook
  const handleUserDelete = async (userId) => {
    const userToDelete = users.find(u => u.id === userId);
    await deleteUser(userToDelete || userId);
  };

  const handleRoleDelete = async (roleId) => {
    const roleToDelete = roles.find(r => r.id === roleId);
    await deleteRole(roleToDelete || roleId);
  };

  const handleInvitationDelete = async (invitationId) => {
    await deleteInvitation(invitationId);
  };

  // Handle product updates
  const handleProductUpdate = async (updatedProduct) => {
    try {
      await adminApi.products.update(updatedProduct.id, updatedProduct);
      // Refresh product detail and overview to sync
      await refreshProductDetail();
      await refreshProductsOverview();
    } catch (error) {
      console.error('Failed to update product:', error);
      throw error; // Let the component handle the error
    }
  };

  // Handle product deletion
  const handleProductDelete = async (productId) => {
    const productToDelete = products.find(p => p.id === productId);
    await deleteProduct(productToDelete || productId);
  };

  // Handle product creation
  const handleProductCreated = async (createdProduct) => {
    setShowCreateProductModal(false);
    await refreshProductsOverview();
    // Select the newly created product
    if (createdProduct?.id) {
      setSelectedProductId(createdProduct.id);
    }
  };

  // Handle action button clicks from sidebar
  const handleActionButtonClick = (buttonId) => {
    if (buttonId === 'parameters') {
      setShowParametersModal(true);
    } else if (buttonId === 'connections') {
      setShowConnectionsModal(true);
    } else if (buttonId === 'models') {
      setShowModelsModal(true);
    } else {
      setActionButtonTrigger({ buttonId, timestamp: Date.now() });
    }
  };

  // Handle restore actions when action button is clicked
  useEffect(() => {
    const executeRestoreAction = async () => {
      if (!actionButtonTrigger.buttonId) return;

      try {
        let result;
        switch (actionButtonTrigger.buttonId) {
          case 'resetToInitial':
            console.log('Executing resetToInitial...');
            result = await adminApi.restore.resetToInitial();
            console.log('Reset to initial result:', result);
            // Clear selected config ID before refresh (configs have new IDs)
            setAdminSelectedConfig(null);
            // Refresh the configuration list
            await refreshAiOverview();
            break;
          case 'restoreFromDefaults':
            console.log('Executing restoreFromDefaults...');
            result = await adminApi.restore.restoreFromDefaults();
            console.log('Restore from defaults result:', result);
            // Clear selected config ID before refresh (configs have new IDs)
            setAdminSelectedConfig(null);
            await refreshAiOverview();
            break;
          case 'setCurrentAsDefault':
            console.log('Executing setCurrentAsDefault...');
            result = await adminApi.restore.setCurrentAsDefault();
            console.log('Set current as default result:', result);
            break;
          default:
            console.warn('Unknown action button:', actionButtonTrigger.buttonId);
        }
      } catch (error) {
        console.error('Restore action failed:', error);
        alert(`Failed to execute action: ${error.message}`);
      }
    };

    executeRestoreAction();
  }, [actionButtonTrigger]);

  // Handle configuration updates
  const handleConfigurationUpdate = async (updates) => {
    if (!selectedConfigId) {
      console.error('No configuration selected');
      throw new Error('No configuration selected');
    }

    try {
      // Send update request (returns full updated configuration)
      const updatedConfig = await adminApi.modelConfigurations.update(selectedConfigId, updates);

      // Refresh the configuration to ensure state is synced
      await refreshConfiguration();

      // Sync overview state to update sidebar
      await refreshAiOverview();
    } catch (error) {
      console.error('Failed to update configuration:', error);
      throw error;
    }
  };

  // Handle configuration deletion
  const handleConfigurationDelete = async (configId) => {
    const configToDelete = aiOverview?.model_configurations?.find(c => c.id === configId);
    await deleteConfiguration(configToDelete || configId, { skipConfirm: true });
  };

  // Sidebar configuration - responsive mode
  const sidebarWidth = 64; // 256px
  const sidebarMode = windowWidth <= 980 ? 'overlay' : 'push';
  const sidebarWidthPx = sidebarWidth * 4;

  // Tab configuration
  const tabs = [
    {
      id: 'users',
      label: 'Users',
      icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z'
    },
    {
      id: 'products',
      label: 'Products',
      icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4'
    },
    {
      id: 'roles',
      label: 'Roles',
      icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
    },
    {
      id: 'permissions',
      label: 'Permissions',
      icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z'
    },
    {
      id: 'ai',
      label: 'AI',
      icon: 'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z'
    },
  ];

  // Context-aware refresh function for AdminSidebar
  const handleRefreshOverview = async () => {
    switch (activeTab) {
      case 'users':
        await refreshUsersOverview();
        break;
      case 'products':
        await refreshProductsOverview();
        break;
      case 'roles':
        await refreshRolesOverview();
        break;
      case 'permissions':
        await refreshPermissionsOverview();
        break;
      case 'ai':
        await refreshAiOverview();
        break;
      default:
        break;
    }
  };

  // Navigation helpers for detail views when in overlay mode
  const getDetailViewNavigation = () => {
    const isOverlayMode = sidebarMode === 'overlay';

    if (!isOverlayMode) {
      return { onPrevious: null, onNext: null };
    }

    if (activeTab === 'users') {
      const currentIndex = users.findIndex(user => user.id === selectedUserId);
      const canGoPrev = currentIndex > 0;
      const canGoNext = currentIndex < users.length - 1;

      return {
        onPrevious: canGoPrev ? () => handleSetSelectedUser(users[currentIndex - 1].id) : null,
        onNext: canGoNext ? () => handleSetSelectedUser(users[currentIndex + 1].id) : null
      };
    } else if (activeTab === 'products') {
      const currentIndex = products.findIndex(product => product.id === selectedProductId);
      const canGoPrev = currentIndex > 0;
      const canGoNext = currentIndex < products.length - 1;

      return {
        onPrevious: canGoPrev ? () => setSelectedProductId(products[currentIndex - 1].id) : null,
        onNext: canGoNext ? () => setSelectedProductId(products[currentIndex + 1].id) : null
      };
    } else if (activeTab === 'roles') {
      const currentIndex = roles?.findIndex(role => role.id === selectedRoleId) ?? -1;
      const canGoPrev = currentIndex > 0;
      const canGoNext = currentIndex < (roles?.length || 0) - 1;

      return {
        onPrevious: canGoPrev ? () => setSelectedRoleId(roles[currentIndex - 1].id) : null,
        onNext: canGoNext ? () => setSelectedRoleId(roles[currentIndex + 1].id) : null
      };
    } else if (activeTab === 'permissions') {
      const currentIndex = permissions.findIndex(perm => perm.id === selectedPermissionId);
      const canGoPrev = currentIndex > 0;
      const canGoNext = currentIndex < permissions.length - 1;

      return {
        onPrevious: canGoPrev ? () => setSelectedPermissionId(permissions[currentIndex - 1].id) : null,
        onNext: canGoNext ? () => setSelectedPermissionId(permissions[currentIndex + 1].id) : null
      };
    } else if (activeTab === 'ai') {
      const currentIndex = modelConfigs.findIndex(config => config.id === selectedConfigId);
      const canGoPrev = currentIndex > 0;
      const canGoNext = currentIndex < modelConfigs.length - 1;

      return {
        onPrevious: canGoPrev ? () => setSelectedConfigId(modelConfigs[currentIndex - 1].id) : null,
        onNext: canGoNext ? () => setSelectedConfigId(modelConfigs[currentIndex + 1].id) : null
      };
    }

    return { onPrevious: null, onNext: null };
  };

  const navigation = getDetailViewNavigation();

  // Get theme from the selected item (theme is attached to each item during data loading)
  const getSelectedItemTheme = () => {
    if (activeTab === 'users') {
      if (selectedUser) return selectedUser.theme || 'purple';
      if (selectedInvitation) return selectedInvitation.theme || 'purple';
    } else if (activeTab === 'products' && selectedProduct) {
      return selectedProduct.theme || 'purple';
    } else if (activeTab === 'roles' && selectedRole) {
      return selectedRole.theme || 'purple';
    } else if (activeTab === 'permissions' && selectedPermission) {
      return selectedPermission.theme || 'purple';
    } else if (activeTab === 'ai' && selectedConfiguration) {
      return selectedConfiguration.theme || 'purple';
    }
    return 'purple'; // fallback
  };

  const selectedItemTheme = getSelectedItemTheme();

  const renderTabContent = () => {
    switch (activeTab) {
      case 'users':
        // Show invitation detail view if an invitation is selected
        if (selectedInvitationId && selectedInvitation) {
          return (
            <InvitationDetailView
              key={selectedInvitationId}
              invitation={selectedInvitation}
              onDelete={handleInvitationDelete}
              theme={selectedItemTheme}
            />
          );
        }
        // Otherwise show user detail view
        return (
          <UserDetailView
            key={selectedUserId}
            userId={selectedUserId}
            user={selectedUser}
            onUpdate={handleUserUpdate}
            onDelete={handleUserDelete}
            onPrevious={navigation.onPrevious}
            onNext={navigation.onNext}
            theme={selectedItemTheme}
          />
        );
      case 'products':
        return (
          <ProductDetailView
            key={selectedProductId}
            product={selectedProduct}
            onUpdate={handleProductUpdate}
            onDelete={handleProductDelete}
            onCreateNew={() => setShowCreateProductModal(true)}
            onPrevious={navigation.onPrevious}
            onNext={navigation.onNext}
            theme={selectedItemTheme}
          />
        );
      case 'roles':
        // Show loading spinner if loading and no data yet
        if (isLoadingRole && !selectedRole) {
          return (
            <div className="h-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
          );
        }
        return (
          <RoleDetailView
            key={selectedRoleId}
            role={selectedRole}
            onDelete={handleRoleDelete}
            onPrevious={navigation.onPrevious}
            onNext={navigation.onNext}
            theme={selectedItemTheme}
          />
        );
      case 'permissions':
        // Show loading spinner if loading and no data yet
        if (isLoadingPermission && !selectedPermission) {
          return (
            <div className="h-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
          );
        }
        return (
          <PermissionDetailView
            key={selectedPermissionId}
            permission={selectedPermission}
            onPrevious={navigation.onPrevious}
            onNext={navigation.onNext}
            theme={selectedItemTheme}
          />
        );
      case 'ai':
        // Only show loading spinner if there's no configuration at all (first load)
        // When switching configs, keep showing previous config to avoid flash
        if (isLoadingConfig && !selectedConfiguration) {
          return (
            <div className="h-full flex items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
            </div>
          );
        }
        return (
          <ModelDetailView
            key={selectedConfigId}
            configId={selectedConfigId}
            configuration={selectedConfiguration}
            onUpdate={handleConfigurationUpdate}
            onDelete={handleConfigurationDelete}
            onRefreshConfiguration={refreshConfigurationAndOverview}
            aiOverview={aiOverview}
            onPrevious={navigation.onPrevious}
            onNext={navigation.onNext}
            theme={selectedItemTheme}
          />
        );
      default:
        return (
          <UserDetailView
            key={selectedUserId}
            userId={selectedUserId}
            user={selectedUser}
            onUpdate={handleUserUpdate}
            onDelete={handleUserDelete}
            onPrevious={navigation.onPrevious}
            onNext={navigation.onNext}
            theme={selectedItemTheme}
          />
        );
    }
  };

  return (
    <div className="h-screen w-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 overflow-hidden" style={{ color: 'var(--color-text-on-accent)' }}>
          {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px] pointer-events-none"></div>

      {/* Animated background accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-600/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
      </div>

      {/* Sidebar */}
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        setIsCollapsed={setIsSidebarCollapsed}
        width={sidebarWidth}
        mode={sidebarMode}
        autoCollapseOnOverlay={true}
      >
        <AdminSidebar
          activeTab={activeTab}
          onActionButtonClick={handleActionButtonClick}
          selectedConfigId={selectedConfigId}
          onConfigSelect={setSelectedConfigId}
          aiOverview={aiOverview}
          modelConfigs={modelConfigs}
          isLoadingOverview={isLoadingOverview}
          onRefreshOverview={handleRefreshOverview}
          users={users}
          selectedUserId={selectedUserId}
          onUserSelect={handleSetSelectedUser}
          invitations={invitations}
          selectedInvitationId={selectedInvitationId}
          onInvitationSelect={handleSetSelectedInvitation}
          onRefreshInvitations={refreshInvitations}
          products={products}
          selectedProductId={selectedProductId}
          onProductSelect={setSelectedProductId}
          onProductDelete={handleProductDelete}
          isLoadingProducts={isLoadingProducts}
          roles={roles}
          selectedRoleId={selectedRoleId}
          onRoleSelect={setSelectedRoleId}
          isLoadingRoles={isLoadingRoles}
          permissions={permissions}
          selectedPermissionId={selectedPermissionId}
          onPermissionSelect={setSelectedPermissionId}
          isLoadingPermissions={isLoadingPermissions}
        />
      </Sidebar>

      {/* Main Content */}
      <main
        className={`fixed top-0 right-0 bottom-0 overflow-hidden transition-all duration-300 flex flex-col z-10`}
        style={{
          left: sidebarMode === 'overlay'
            ? '0'
            : isSidebarCollapsed
              ? '0'
              : `${sidebarWidthPx}px`
        }}
      >
        {/* Header with navigation tabs and X button - Same color as sidebar */}
        <div className="h-16 border-b flex items-center justify-between px-6 flex-shrink-0" style={{ backgroundColor: 'var(--color-bg-elevated)', borderColor: 'var(--color-border-default)' }}>
          {/* Navigation Tabs */}
          <div className="flex items-center space-x-2">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={(e) => {
                  handleSetActiveTab(tab.id);
                  e.currentTarget.blur(); // Remove focus after click
                }}
                className={`px-4 py-2 rounded-lg flex items-center ${windowWidth > 650 ? 'space-x-2' : 'justify-center'} transition-all duration-200 ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-br from-purple-600 to-blue-600 shadow-lg'
                    : ''
                }`}
                style={activeTab === tab.id ? { color: '#ffffff' } : { color: 'var(--color-text-muted)' }}
                title={windowWidth <= 650 ? tab.label : ''}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.color = 'var(--color-text-primary)';
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.color = 'var(--color-text-muted)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
                onFocus={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.color = 'var(--color-text-primary)';
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                  }
                }}
                onBlur={(e) => {
                  if (activeTab !== tab.id) {
                    e.currentTarget.style.color = 'var(--color-text-muted)';
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
                </svg>
                {windowWidth > 650 && <span className="text-sm font-semibold">{tab.label}</span>}
              </button>
            ))}
          </div>

          {/* Exit button */}
          <IconButton onClick={() => navigate('/home')} title="Exit Admin">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </IconButton>
        </div>

        {/* Tab Content */}
        <div className="flex-1 overflow-y-auto p-0">
          {renderTabContent()}
        </div>
      </main>

      {/* Parameters Modal */}
      {showParametersModal && (
        <AIParametersModal
          isOpen={showParametersModal}
          onClose={() => setShowParametersModal(false)}
          parameters={aiOverview?.parameters || []}
          isLoading={isLoadingOverview}
          onRefresh={refreshAiOverview}
        />
      )}

      {/* Connections Modal */}
      {showConnectionsModal && (
        <AIConnectionsModal
          isOpen={showConnectionsModal}
          onClose={() => setShowConnectionsModal(false)}
          connections={aiOverview?.connections || []}
          providers={aiOverview?.providers || []}
          isLoading={isLoadingOverview}
          onRefresh={refreshAiOverview}
        />
      )}

      {/* Models Modal */}
      {showModelsModal && (
        <AIModelsModal
          isOpen={showModelsModal}
          onClose={() => setShowModelsModal(false)}
          models={aiOverview?.models || []}
          providers={aiOverview?.providers || []}
          isLoading={isLoadingOverview}
          onRefresh={refreshAiOverview}
        />
      )}

      {/* Create Product Modal */}
      {showCreateProductModal && (
        <CreateProductModal
          isOpen={showCreateProductModal}
          onClose={() => setShowCreateProductModal(false)}
          onProductCreated={handleProductCreated}
        />
      )}

      {/* Delete Confirmation Modals */}
      <DeleteConfirmationModal {...userDeleteModal} />
      <DeleteConfirmationModal {...roleDeleteModal} />
      <DeleteConfirmationModal {...invitationDeleteModal} />
      <DeleteConfirmationModal {...productDeleteModal} />
      <DeleteConfirmationModal {...configDeleteModal} />

      {/* Floating Action Menu */}
      <FloatingActionMenu
        collections={collections}
        products={knowledgeProducts}
        onRefreshProducts={fetchProducts}
        onRefreshCollections={fetchCollections}
      />

    </div>
  );
}
